<template>
  <layout title="Cobranca" :options="options" :rotas="rotas">
    <section class="collection-page">
      <div class="collection-toolbar">
        <v-text-field
          dense
          filled
          hide-details
          clearable
          label="Buscar cliente ou venda"
          prepend-inner-icon="mdi-magnify"
          v-model="filters.search"
          @input="debounceLoad"
        />
        <v-menu v-model="startMenu" :close-on-content-click="false" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              dense
              filled
              hide-details
              readonly
              clearable
              label="Inicio"
              prepend-inner-icon="mdi-calendar"
              v-bind="attrs"
              v-on="on"
              v-model="filters.date_from"
              @click:clear="loadData"
            />
          </template>
          <v-date-picker v-model="filters.date_from" @input="startMenu = false; loadData()" />
        </v-menu>
        <v-menu v-model="endMenu" :close-on-content-click="false" offset-y min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              dense
              filled
              hide-details
              readonly
              clearable
              label="Fim"
              prepend-inner-icon="mdi-calendar"
              v-bind="attrs"
              v-on="on"
              v-model="filters.date_to"
              @click:clear="loadData"
            />
          </template>
          <v-date-picker v-model="filters.date_to" @input="endMenu = false; loadData()" />
        </v-menu>
      </div>

      <div class="collection-summary">
        <v-card class="collection-summary-card" elevation="0">
          <span>Vendas pendentes</span>
          <strong>{{ pendingSales.length }}</strong>
        </v-card>
        <v-card class="collection-summary-card" elevation="0">
          <span>Total em aberto</span>
          <strong>{{ formatMoney(totalDue) }}</strong>
        </v-card>
        <v-card class="collection-summary-card" elevation="0">
          <span>Tempo medio de entrega</span>
          <strong>{{ averageDeliveryLabel }}</strong>
        </v-card>
      </div>

      <v-card class="collection-table-card" elevation="0">
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Venda</th>
                <th>Cliente</th>
                <th>Entrega concluida</th>
                <th>Total</th>
                <th>Pago</th>
                <th>Pendente</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!loading && pendingSales.length === 0">
                <td colspan="7">
                  <div class="collection-empty">
                    <v-icon size="42" color="#9aa7a0">mdi-check-circle-outline</v-icon>
                    <strong>Nenhuma pendencia pos-entrega</strong>
                  </div>
                </td>
              </tr>
              <tr v-for="sale in pendingSales" :key="sale._id">
                <td>#{{ sale.id_transacao || sale._id }}</td>
                <td>
                  <strong>{{ clientName(sale) }}</strong>
                  <small>{{ clientPhone(sale) }}</small>
                </td>
                <td>
                  {{ formatDate(sale.collection && sale.collection.delivery_done_at) }}
                  <small>{{ sale.collection && sale.collection.days_since_delivery }} dias</small>
                </td>
                <td>{{ formatMoney(sale.collection && sale.collection.sale_total) }}</td>
                <td>{{ formatMoney(sale.collection && sale.collection.paid_total) }}</td>
                <td class="red--text font-weight-bold">
                  {{ formatMoney(sale.collection && sale.collection.amount_due) }}
                </td>
                <td class="collection-actions">
                  <v-btn
                    small
                    depressed
                    class="collection-view-button text-none"
                    @click="openDetailDialog(sale)"
                  >
                    Visualizar
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </section>

    <v-dialog v-model="detailDialog" max-width="920" scrollable>
      <v-card v-if="selectedSale" class="collection-detail-card">
        <v-card-title class="collection-detail-title">
          <div>
            <span class="collection-detail-kicker">Cobranca pendente</span>
            <h2>#{{ saleNumber(selectedSale) }}</h2>
            <small>
              Entrega concluida em {{ formatDateTime(selectedSale.collection && selectedSale.collection.delivery_done_at) }}
            </small>
          </div>
        </v-card-title>

        <v-card-text class="collection-detail-body">
          <div class="collection-detail-summary">
            <div>
              <span>Total da venda</span>
              <strong>{{ formatMoney(selectedSale.collection && selectedSale.collection.sale_total) }}</strong>
            </div>
            <div>
              <span>Valor pago</span>
              <strong>{{ formatMoney(selectedSale.collection && selectedSale.collection.paid_total) }}</strong>
            </div>
            <div class="collection-detail-due">
              <span>Pendente</span>
              <strong>{{ formatMoney(selectedSale.collection && selectedSale.collection.amount_due) }}</strong>
            </div>
            <div>
              <span>Pos-entrega</span>
              <strong>{{ selectedSale.collection && selectedSale.collection.days_since_delivery }} dias</strong>
            </div>
          </div>

          <div class="collection-detail-grid">
            <section class="collection-detail-section">
              <h3>Cliente</h3>
              <dl>
                <div>
                  <dt>Nome</dt>
                  <dd>{{ clientName(selectedSale) }}</dd>
                </div>
                <div>
                  <dt>Telefone</dt>
                  <dd>{{ clientPhone(selectedSale) || "Nao informado" }}</dd>
                </div>
                <div>
                  <dt>E-mail</dt>
                  <dd>{{ clientEmail(selectedSale) || "Nao informado" }}</dd>
                </div>
                <div>
                  <dt>Documento</dt>
                  <dd>{{ clientDocument(selectedSale) || "Nao informado" }}</dd>
                </div>
              </dl>
            </section>

            <section class="collection-detail-section">
              <h3>Entrega</h3>
              <dl>
                <div>
                  <dt>Endereco</dt>
                  <dd>{{ deliveryAddress(selectedSale) }}</dd>
                </div>
                <div>
                  <dt>Unidade</dt>
                  <dd>{{ saleUnityName(selectedSale) }}</dd>
                </div>
                <div>
                  <dt>Responsavel</dt>
                  <dd>{{ deliveryResponsible(selectedSale) }}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>Entrega concluida</dd>
                </div>
              </dl>
            </section>
          </div>

          <section class="collection-detail-section">
            <h3>Produtos</h3>
            <div v-if="saleProducts(selectedSale).length" class="collection-products">
              <div v-for="(product, index) in saleProducts(selectedSale)" :key="`product-${index}`">
                <span>{{ productName(product) }}</span>
                <small>{{ productQuantity(product) }} x {{ formatMoney(productPrice(product)) }}</small>
              </div>
            </div>
            <div v-else class="collection-muted-box">Nenhum produto detalhado nesta venda.</div>
          </section>

          <div class="collection-detail-grid">
            <section class="collection-detail-section">
              <h3>Historico de pagamento</h3>
              <div v-if="paymentHistory(selectedSale).length" class="collection-history-list">
                <div v-for="(payment, index) in paymentHistory(selectedSale)" :key="`payment-${index}`">
                  <strong>{{ formatMoney(payment.valor) }}</strong>
                  <span>{{ payment.forma_de_pagamento || payment.payment_method_name || "Pagamento" }}</span>
                  <small>{{ formatDateTime(payment.created_at || payment.data || payment.date) }}</small>
                </div>
              </div>
              <div v-else class="collection-muted-box">Nenhum pagamento registrado.</div>
            </section>

            <section class="collection-detail-section">
              <h3>Contatos de cobranca</h3>
              <div v-if="collectionContacts(selectedSale).length" class="collection-history-list">
                <div v-for="(contact, index) in collectionContacts(selectedSale)" :key="`contact-${index}`">
                  <strong>{{ contact.description || "Contato registrado" }}</strong>
                  <span>{{ channelLabel(contact.channel) }}</span>
                  <small>{{ formatDateTime(contact.created_at) }}</small>
                </div>
              </div>
              <div v-else class="collection-muted-box">Nenhum contato registrado ainda.</div>
            </section>
          </div>
        </v-card-text>

        <v-card-actions class="collection-detail-actions">
          <v-btn text class="text-none" @click="detailDialog = false">Fechar</v-btn>
          <v-spacer />
          <v-btn outlined class="text-none" color="#075e54" @click="openWhatsapp(selectedSale)">
            Abrir IntraWhats
          </v-btn>
          <v-btn
            outlined
            class="text-none"
            color="#075e54"
            :loading="actionLoading === `reminder-${selectedSale._id}`"
            @click="sendReminder(selectedSale)"
          >
            Enviar cobranca
          </v-btn>
          <v-btn outlined class="text-none" color="#075e54" @click="openContactDialog(selectedSale)">
            Registrar contato
          </v-btn>
          <v-btn depressed class="text-none" color="#1f8f55" dark @click="openPaymentDialog(selectedSale)">
            Baixar pagamento
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="contactDialog" max-width="520">
      <v-card>
        <v-card-title>Registrar contato</v-card-title>
        <v-card-text>
          <v-select
            filled
            dense
            label="Canal"
            :items="contactChannels"
            v-model="contactForm.channel"
          />
          <v-textarea
            filled
            dense
            label="Resumo"
            v-model="contactForm.description"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="contactDialog = false">Cancelar</v-btn>
          <v-btn color="#1f8f55" dark :loading="actionLoading === 'contact'" @click="registerContact">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="paymentDialog" max-width="520">
      <v-card>
        <v-card-title>Baixar pagamento</v-card-title>
        <v-card-text>
          <v-text-field
            filled
            dense
            type="number"
            label="Valor"
            prefix="R$"
            v-model="paymentForm.amount"
          />
          <v-select
            filled
            dense
            label="Forma"
            item-text="text"
            item-value="value"
            :items="paymentMethods"
            :disabled="paymentMethods.length === 0"
            v-model="paymentForm.payment_method_key"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="paymentDialog = false">Cancelar</v-btn>
          <v-btn color="#1f8f55" dark :loading="actionLoading === 'payment'" @click="markPaid">Baixar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </layout>
</template>

<script>
import _ from "lodash";

export default {
  data() {
    return {
      loading: false,
      actionLoading: "",
      pendingSales: [],
      averageDelivery: {
        total_deliveries: 0,
        average_days: 0,
        average_hours: 0
      },
      filters: {
        search: "",
        date_from: "",
        date_to: ""
      },
      startMenu: false,
      endMenu: false,
      detailDialog: false,
      contactDialog: false,
      paymentDialog: false,
      selectedSale: null,
      contactForm: {
        channel: "phone",
        description: ""
      },
      paymentForm: {
        amount: 0,
        payment_method_key: "dinheiro"
      },
      payment_sys_config: {
        money: { active: true, tax_porcentage: 0 },
        card: { active: false, tax_porcentage: 0 },
        pix: { active: false, tax_porcentage: 0 },
        debit: { active: false, tax_porcentage: 0 },
        finishing: { active: false, tax_porcentage: 30 },
        custom_methods: []
      },
      contactChannels: [
        { text: "Telefone", value: "phone" },
        { text: "WhatsApp", value: "whatsapp" },
        { text: "Presencial", value: "in_person" },
        { text: "Outro", value: "other" }
      ],
      rotas: [
        { name: "Inicio", path: "/dashboard" },
        { name: "Cobranca", path: "/cobranca" }
      ],
      options: [
        {
          icon: "mdi-update",
          nome: "Atualizar",
          action: () => this.loadData()
        }
      ]
    };
  },
  computed: {
    totalDue() {
      return this.pendingSales.reduce(
        (total, sale) => total + Number(sale.collection && sale.collection.amount_due || 0),
        0
      );
    },
    averageDeliveryLabel() {
      if (!this.averageDelivery.total_deliveries) return "Sem entregas";
      if (this.averageDelivery.average_days >= 1) {
        return `${this.averageDelivery.average_days} dias`;
      }
      return `${this.averageDelivery.average_hours} horas`;
    },
    normalizedPaymentConfig() {
      return this.normalizePaymentSysConfig(this.payment_sys_config);
    },
    paymentMethods() {
      const config = this.normalizedPaymentConfig;
      const legacyMethods = [
        { configKey: "money", text: "Dinheiro", value: "dinheiro", label: "dinheiro" },
        { configKey: "pix", text: "Pix", value: "pix", label: "pix" },
        { configKey: "card", text: "Credito", value: "credito", label: "credito" },
        { configKey: "debit", text: "Debito", value: "debito", label: "debito" }
      ].filter(method => config[method.configKey] && config[method.configKey].active);

      const customMethods = config.custom_methods
        .filter(method => method && method.active !== false && method.name)
        .map((method, index) => ({
          text: method.name,
          value: method.id || `custom_${index + 1}`,
          label: method.name
        }));

      return [...legacyMethods, ...customMethods];
    }
  },
  methods: {
    normalizePaymentSysConfig(config = {}) {
      return {
        money: { tax_porcentage: 0, ...(config.money || {}), active: true },
        card: { active: false, tax_porcentage: 0, ...(config.card || {}) },
        pix: { active: false, tax_porcentage: 0, ...(config.pix || {}) },
        debit: { active: false, tax_porcentage: 0, ...(config.debit || {}) },
        finishing: { active: false, tax_porcentage: 30, ...(config.finishing || {}) },
        custom_methods: Array.isArray(config.custom_methods) ? config.custom_methods : []
      };
    },
    loadPaymentConfig() {
      return this.$run("marketplaces/get-config-payment-sys")
        .then(res => {
          this.payment_sys_config = this.normalizePaymentSysConfig(res.data.payment_sys_config);
        })
        .catch(() => {
          this.payment_sys_config = this.normalizePaymentSysConfig(this.payment_sys_config);
        });
    },
    loadData() {
      this.loading = true;
      const payload = { ...this.filters };
      Promise.all([
        this.$run("cobranca/list", payload),
        this.$run("cobranca/avg-delivery-time", payload)
      ])
        .then(([listRes, avgRes]) => {
          this.pendingSales = listRes.data.docs || [];
          this.averageDelivery = avgRes.data || this.averageDelivery;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    debounceLoad: _.debounce(function() {
      this.loadData();
    }, 500),
    clientName(sale) {
      return sale.client && sale.client.nome || "Cliente nao informado";
    },
    clientPhone(sale) {
      return sale.client && (sale.client.telefone || sale.client.whatsapp_phone) || "";
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    },
    formatDate(value) {
      if (!value) return "-";
      return this.$moment(value).format("DD/MM/YYYY");
    },
    formatDateTime(value) {
      if (!value) return "-";
      return this.$moment(value).format("DD/MM/YYYY [as] HH:mm");
    },
    saleNumber(sale) {
      return sale && (sale.id_transacao || sale._id) || "-";
    },
    openWhatsapp(sale) {
      const clientId = sale.client && sale.client._id;
      this.$router.push({
        path: "/whatsapp-atendimento",
        query: { clientId, saleId: sale._id }
      });
    },
    openDetailDialog(sale) {
      this.selectedSale = sale;
      this.detailDialog = true;
    },
    openContactDialog(sale) {
      this.selectedSale = sale;
      this.contactForm = {
        channel: "phone",
        description: ""
      };
      this.contactDialog = true;
    },
    registerContact() {
      if (!this.selectedSale) return;
      this.actionLoading = "contact";
      this.$run("cobranca/register-contact", {
        transaction_id: this.selectedSale._id,
        ...this.contactForm
      })
        .then(() => {
          this.contactDialog = false;
          this.loadData();
          if (this.detailDialog) {
            this.detailDialog = false;
          }
        })
        .finally(() => {
          this.actionLoading = "";
        });
    },
    sendReminder(sale) {
      this.actionLoading = `reminder-${sale._id}`;
      this.$run("cobranca/send-reminder", {
        transaction_id: sale._id
      })
        .then(() => this.loadData())
        .finally(() => {
          this.actionLoading = "";
        });
    },
    openPaymentDialog(sale) {
      this.selectedSale = sale;
      const defaultMethod = this.paymentMethods[0] || { value: "dinheiro" };
      this.paymentForm = {
        amount: sale.collection && sale.collection.amount_due || 0,
        payment_method_key: defaultMethod.value
      };
      this.paymentDialog = true;
    },
    markPaid() {
      if (!this.selectedSale) return;
      const method = this.paymentMethods.find(item => item.value === this.paymentForm.payment_method_key);
      this.actionLoading = "payment";
      this.$run("cobranca/mark-paid", {
        transaction_id: this.selectedSale._id,
        amount: this.paymentForm.amount,
        payment_method_key: this.paymentForm.payment_method_key,
        payment_method_name: method ? method.label : this.paymentForm.payment_method_key
      })
        .then(() => {
          this.paymentDialog = false;
          this.detailDialog = false;
          this.loadData();
        })
        .finally(() => {
          this.actionLoading = "";
        });
    },
    saleClient(sale) {
      return sale && (sale.client || sale.compra && sale.compra.cliente) || {};
    },
    clientEmail(sale) {
      const client = this.saleClient(sale);
      return client.email || client.mail || "";
    },
    clientDocument(sale) {
      const client = this.saleClient(sale);
      return client.cpfCnpj || client.cpf_cnpj || client.cpf || client.cnpj || "";
    },
    addressText(address) {
      if (!address) return "";
      if (typeof address === "string") return address;
      return [
        address.formatted_address,
        address.street || address.logradouro || address.endereco,
        address.number || address.numero,
        address.bairro || address.district,
        address.city || address.cidade,
        address.state || address.uf,
        address.zip_code || address.cep
      ].filter(Boolean).join(", ");
    },
    deliveryAddress(sale) {
      const delivery = sale && sale.collection && sale.collection.delivery || {};
      const compra = sale && sale.compra || {};
      const client = this.saleClient(sale);
      return this.addressText(delivery.address_destiny) ||
        this.addressText(delivery.destination) ||
        this.addressText(delivery.delivery_address) ||
        this.addressText(compra.address_destiny) ||
        this.addressText(compra.delivery_address) ||
        this.addressText(compra.endereco) ||
        this.addressText(client.address) ||
        this.addressText(client.endereco) ||
        "Endereco nao informado";
    },
    saleUnityName(sale) {
      const delivery = sale && sale.collection && sale.collection.delivery || {};
      const unity = delivery.unity || sale && sale.unity || {};
      return unity.nome || unity.name || "Nao informada";
    },
    deliveryResponsible(sale) {
      const delivery = sale && sale.collection && sale.collection.delivery || {};
      const executor = delivery.executor || delivery.responsible || {};
      return executor.nome || executor.name || "Nao informado";
    },
    saleProducts(sale) {
      const compra = sale && sale.compra || {};
      return Array.isArray(compra.produtos) ? compra.produtos : [];
    },
    productName(product = {}) {
      return product.nome || product.name || product.descricao || product.description || "Produto";
    },
    productQuantity(product = {}) {
      return product.quantidade || product.quantity || product.qtd || 1;
    },
    productPrice(product = {}) {
      return product.preco || product.price || product.valor || product.value || product.preco_total || 0;
    },
    paymentHistory(sale) {
      return sale && Array.isArray(sale.historico_de_pagamento)
        ? sale.historico_de_pagamento
        : [];
    },
    collectionContacts(sale) {
      const comments = sale && Array.isArray(sale.comments) ? sale.comments : [];
      return comments.filter(comment => comment.source === "collection");
    },
    channelLabel(channel) {
      const item = this.contactChannels.find(channelItem => channelItem.value === channel);
      return item ? item.text : channel || "Manual";
    },
    handleRealtimeSaleUpdated(event) {
      const detail = event && event.detail || {};
      const type = detail.event && detail.event.type || "";
      if (type && !["collection", "payment", "delivery", "status"].includes(type)) return;
      this.debounceLoad();
    }
  },
  created() {
    this.loadPaymentConfig();
    this.loadData();
    this.collectionRealtimeHandler = event => this.handleRealtimeSaleUpdated(event);
    window.addEventListener("stonepro:sale-updated", this.collectionRealtimeHandler);
  },
  beforeDestroy() {
    window.removeEventListener("stonepro:sale-updated", this.collectionRealtimeHandler);
  }
};
</script>

<style scoped>
.collection-page {
  background: #f5f7f4;
  min-height: calc(100vh - 72px);
  padding: 18px;
}
.collection-toolbar {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(220px, 1fr) 170px 170px;
  margin-bottom: 14px;
}
.collection-summary {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 14px;
}
.collection-summary-card {
  border: 1px solid #e1e8dd;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px;
}
.collection-summary-card span,
td small {
  color: #65746b;
  display: block;
  font-size: 12px;
}
.collection-summary-card strong {
  color: #20332a;
  font-size: 22px;
}
.collection-table-card {
  border: 1px solid #e1e8dd;
  border-radius: 8px;
  overflow: hidden;
}
.collection-empty {
  align-items: center;
  color: #5f6f66;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 220px;
}
.collection-actions {
  text-align: right;
  white-space: nowrap;
}
.collection-view-button {
  background: #e8f4ef !important;
  border: 1px solid #cfe3d8;
  color: #075e54 !important;
  font-weight: 700;
  min-width: 96px;
}
.collection-detail-card {
  border-radius: 8px;
}
.collection-detail-title {
  align-items: flex-start;
  border-bottom: 1px solid #e1e8dd;
  display: flex;
  padding: 22px 24px 18px;
}
.collection-detail-title h2 {
  color: #20332a;
  font-size: 24px;
  line-height: 1.15;
  margin: 2px 0;
}
.collection-detail-title small,
.collection-detail-kicker {
  color: #65746b;
  display: block;
  font-size: 12px;
}
.collection-detail-kicker {
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.collection-detail-body {
  background: #fbfcfa;
  padding: 20px 24px !important;
}
.collection-detail-summary {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 14px;
}
.collection-detail-summary div,
.collection-detail-section {
  background: #fff;
  border: 1px solid #e1e8dd;
  border-radius: 8px;
}
.collection-detail-summary div {
  padding: 14px;
}
.collection-detail-summary span {
  color: #65746b;
  display: block;
  font-size: 12px;
}
.collection-detail-summary strong {
  color: #20332a;
  display: block;
  font-size: 20px;
  line-height: 1.25;
  margin-top: 4px;
}
.collection-detail-summary .collection-detail-due strong {
  color: #c03221;
}
.collection-detail-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 12px;
}
.collection-detail-section {
  margin-bottom: 12px;
  padding: 16px;
}
.collection-detail-section h3 {
  color: #20332a;
  font-size: 16px;
  margin: 0 0 12px;
}
.collection-detail-section dl {
  display: grid;
  gap: 10px;
  margin: 0;
}
.collection-detail-section dl div {
  display: grid;
  gap: 2px;
}
.collection-detail-section dt {
  color: #65746b;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.collection-detail-section dd {
  color: #20332a;
  font-size: 13px;
  margin: 0;
  word-break: break-word;
}
.collection-products,
.collection-history-list {
  display: grid;
  gap: 8px;
}
.collection-products div,
.collection-history-list div {
  background: #f7faf6;
  border: 1px solid #edf2ea;
  border-radius: 8px;
  padding: 10px 12px;
}
.collection-products span,
.collection-history-list strong {
  color: #20332a;
  display: block;
  font-size: 13px;
}
.collection-products small,
.collection-history-list span,
.collection-history-list small,
.collection-muted-box {
  color: #65746b;
  display: block;
  font-size: 12px;
}
.collection-muted-box {
  background: #f7faf6;
  border: 1px dashed #d7e3dc;
  border-radius: 8px;
  padding: 14px;
  text-align: center;
}
.collection-detail-actions {
  border-top: 1px solid #e1e8dd;
  flex-wrap: wrap;
  gap: 8px;
  padding: 14px 24px !important;
}
@media(max-width: 960px) {
  .collection-toolbar,
  .collection-summary,
  .collection-detail-summary,
  .collection-detail-grid {
    grid-template-columns: 1fr;
  }
  .collection-detail-actions .spacer {
    display: none;
  }
  .collection-detail-actions .v-btn {
    width: 100%;
  }
}
</style>
